import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { ChevronRight, ChevronLeft, X, Maximize2 } from 'lucide-react';
import { OUR_PROJECTS_IMAGES } from '../utils/imageUrls';
import { useIntersectionObserver } from "./Offers";

const OurProjects = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // אני מניח ש-useIntersectionObserver מחזיר [ref, isVisible] כמו שכתבת במקור
    const [mainRef, mainVisible] = useIntersectionObserver();

    const projects = useMemo(() => [
        { image: OUR_PROJECTS_IMAGES.playHouse, title: "בית עץ" },

        { image: OUR_PROJECTS_IMAGES.gardenPlanters, title: "אדניות לגינה" },
        { image: OUR_PROJECTS_IMAGES.gardenPlanters1, title: "אדניות מעוצבות" },
        { image: OUR_PROJECTS_IMAGES.gardenPlanters3, title: "גילוף בעיצוב אישי" },
        { image: OUR_PROJECTS_IMAGES.storageBox, title: "ארגז אחסון" },
        { image: OUR_PROJECTS_IMAGES.storageBox1, title: "פתרונות אחסון" }
    ], []);

    const nextImage = useCallback(() => {
        setCurrentIndex((prev) => (prev + 1) % projects.length);
    }, [projects.length]);

    const prevImage = useCallback(() => {
        setCurrentIndex((prev) => (prev - 1 + projects.length) % projects.length);
    }, [projects.length]);

    const goToImage = useCallback((index) => {
        setCurrentIndex(index);
    }, []);

    // ניווט מקלדת במודאל ומחוצה לו
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'ArrowRight') prevImage();
            if (e.key === 'ArrowLeft') nextImage();
            if (e.key === 'Escape' && isModalOpen) setIsModalOpen(false);
        };
        document.addEventListener('keydown', handleKeyDown);
        if (isModalOpen) document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'unset';
        };
    }, [isModalOpen, nextImage, prevImage]);

    const currentProject = projects[currentIndex];

    return (
        <section className="py-24 bg-background" dir="rtl">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                {/* כותרת הסקשן */}
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-5xl font-medium text-text-primary tracking-wide mb-4">
                        תיק עבודות
                    </h2>
                    <div className="w-16 h-1 bg-accent mx-auto rounded-full mb-6"></div>
                    <p className="text-lg text-text-secondary max-w-2xl mx-auto font-light">
                        הצצה לפרויקטים נבחרים שיצאו מהסדנא שלנו
                    </p>
                </div>

                {/* --- אזור התצוגה המרכזי --- */}
                <div
                    ref={mainRef}
                    className={`
                        relative max-w-5xl mx-auto mb-8 rounded-2xl overflow-hidden shadow-soft group
                        transition-all duration-1000 ease-out border border-secondary/50
                        ${mainVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}
                    `}
                >
                    {/* התמונה הראשית */}
                    <div className="relative aspect-[16/10] sm:aspect-[21/9] bg-surface">
                        <img
                            src={currentProject.image}
                            alt={currentProject.title}
                            className="w-full h-full object-cover transition-transform duration-700 ease-out cursor-pointer group-hover:scale-[1.02]"
                            onClick={() => setIsModalOpen(true)}
                        />

                        {/* שכבת כהות עדינה למטה להבלטת הטקסט */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-80 pointer-events-none" />

                        {/* טקסט על התמונה */}
                        <div className="absolute bottom-6 right-8 text-white pointer-events-none">
                            <h3 className="text-2xl sm:text-3xl font-medium mb-1 drop-shadow-md">
                                {currentProject.title}
                            </h3>
                            <p className="text-sm text-white/80 font-light">
                                {currentIndex + 1} מתוך {projects.length}
                            </p>
                        </div>

                        {/* כפתור הגדלה (רומז על אינטראקציה במקום "לחץ להגדלה") */}
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="absolute top-6 left-6 p-2 bg-black/30 backdrop-blur-md rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-black/50"
                        >
                            <Maximize2 size={20} />
                        </button>

                        {/* חיצי ניווט סולידיים */}
                        <button onClick={prevImage} className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-surface/90 text-primary rounded-full shadow-md hover:bg-surface hover:text-accent transition-all duration-300 transform translate-x-4 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-accent">
                            <ChevronRight size={24} />
                        </button>
                        <button onClick={nextImage} className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-surface/90 text-primary rounded-full shadow-md hover:bg-surface hover:text-accent transition-all duration-300 transform -translate-x-4 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-accent">
                            <ChevronLeft size={24} />
                        </button>
                    </div>
                </div>

                {/* --- גלריית תמונות ממוזערות בתחתית --- */}
                <div className="max-w-5xl mx-auto">
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 sm:gap-4">
                        {projects.map((project, index) => (
                            <button
                                key={index}
                                onClick={() => goToImage(index)}
                                className={`
                                    relative aspect-square rounded-xl overflow-hidden transition-all duration-300
                                    ${currentIndex === index
                                    ? 'border-2 border-accent shadow-md opacity-100'
                                    : 'border border-secondary opacity-60 hover:opacity-100 hover:border-accent/50'
                                }
                                `}
                            >
                                <img
                                    src={project.image}
                                    alt={project.title}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                            </button>
                        ))}
                    </div>
                </div>

            </div>

            {/* --- מודאל מסך מלא נקי ויוקרתי --- */}
            {isModalOpen && (
                <div
                    className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-4 sm:p-8"
                    onClick={() => setIsModalOpen(false)}
                >
                    <button
                        className="absolute top-6 right-6 p-2 text-white/70 hover:text-white transition-colors"
                        onClick={() => setIsModalOpen(false)}
                    >
                        <X size={32} />
                    </button>

                    <div className="relative max-w-7xl w-full h-full flex flex-col items-center justify-center" onClick={e => e.stopPropagation()}>
                        <img
                            src={currentProject.image}
                            alt={currentProject.title}
                            className="max-h-[85vh] w-auto object-contain rounded-md shadow-2xl"
                        />
                        <h3 className="text-white text-xl mt-6 font-medium tracking-wide">
                            {currentProject.title}
                        </h3>

                        {/* ניווט במודאל */}
                        <button onClick={prevImage} className="absolute right-0 top-1/2 -translate-y-1/2 p-4 text-white/50 hover:text-white transition-colors focus:outline-none">
                            <ChevronRight size={48} />
                        </button>
                        <button onClick={nextImage} className="absolute left-0 top-1/2 -translate-y-1/2 p-4 text-white/50 hover:text-white transition-colors focus:outline-none">
                            <ChevronLeft size={48} />
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
};

export default OurProjects;